// Activation du second facteur, obligatoire pour certains rôles (D-025) : secret à saisir dans
// une application d'authentification, confirmation par un premier code, remise unique des
// codes de secours.
import { Bandeau, Bouton, Champ, Formulaire } from '@cc/ui';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { requete } from '../../api/client';
import { ErreurApi, messageErreur } from '../../api/erreurs';
import { useSession } from '../../session/FournisseurSession';
import commun from '../../commun/commun.module.css';
import styles from './acces.module.css';
import { LogoComplet } from './Logo';
import { CodeQrMfa } from './CodeQrMfa';

interface Secret {
  secret: string;
  uri_otpauth: string;
}

export function PageActivationMfa() {
  const { contexte, activerContexte, recharger } = useSession();
  const navigate = useNavigate();
  const appartenanceId = (useLocation().state as { appartenanceId?: string } | null)
    ?.appartenanceId;
  const [secret, setSecret] = useState<Secret | null>(null);
  const [code, setCode] = useState('');
  const [codesSecours, setCodesSecours] = useState<string[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    document.title = 'Activer le second facteur - CollectivitésConnect';
  }, []);

  const demarrer = async () => {
    setErreur(null);
    setEnCours(true);
    try {
      setSecret(await requete<Secret>('/auth/mfa/activer', { methode: 'POST' }));
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  };

  const confirmer = async () => {
    if (!/^\d{6}$/.test(code)) {
      setErreur('Le code comporte six chiffres.');
      return;
    }
    setErreur(null);
    setEnCours(true);
    try {
      const r = await requete<{ codes_secours: string[] }>('/auth/mfa/confirmer', {
        methode: 'POST',
        corps: { code },
      });
      setCodesSecours(r.codes_secours);
      setSecret(null);
      setCode('');
      await recharger();
    } catch (e) {
      setErreur(
        e instanceof ErreurApi && e.code === 'code_invalide'
          ? "Code incorrect : vérifiez l'heure de votre téléphone."
          : messageErreur(e),
      );
    } finally {
      setEnCours(false);
    }
  };

  const continuer = async () => {
    setEnCours(true);
    try {
      if (appartenanceId) await activerContexte(appartenanceId);
      navigate(appartenanceId ? '/' : '/contexte', { replace: true });
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  };

  const dejaActif = contexte?.compte.mfa_actif && !codesSecours;

  return (
    <div className={styles.page}>
      <LogoComplet />
      <main className={`${styles.carte} ${styles.large}`} id="contenu-principal">
        <h1>Activer le second facteur</h1>
        {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
        {dejaActif ? (
          <>
            <Bandeau type="succes">Le second facteur est déjà actif sur votre compte.</Bandeau>
            <Bouton onClick={() => void continuer()} chargement={enCours}>
              Continuer
            </Bouton>
          </>
        ) : codesSecours ? (
          <>
            <Bandeau type="succes" titre="Second facteur activé">
              Il sera demandé à chaque connexion.
            </Bandeau>
            <p>
              Conservez ces codes de secours en lieu sûr : ils permettent de vous connecter sans
              votre téléphone. Ils ne seront plus jamais affichés et chacun ne sert qu&apos;une
              fois.
            </p>
            <ul className={commun.codes} aria-label="Codes de secours">
              {codesSecours.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
            <Bouton onClick={() => void continuer()} chargement={enCours}>
              J&apos;ai conservé mes codes, continuer
            </Bouton>
          </>
        ) : !secret ? (
          <>
            <p>
              Vos rôles dans cet espace exigent un second facteur. Installez une application
              d&apos;authentification (par exemple FreeOTP ou Aegis), puis lancez l&apos;activation.
            </p>
            <Bouton onClick={() => void demarrer()} chargement={enCours}>
              Commencer l&apos;activation
            </Bouton>
          </>
        ) : (
          <Formulaire
            libelle="Confirmation du second facteur"
            onSoumettre={() => void confirmer()}
            actions={
              <Bouton type="submit" chargement={enCours}>
                Confirmer
              </Bouton>
            }
          >
            <p>
              Dans votre application d&apos;authentification, ajoutez un compte en scannant ce code
              QR :
            </p>
            <CodeQrMfa uri={secret.uri_otpauth} />
            <p>Vous pouvez aussi saisir cette clé manuellement (type « basé sur le temps ») :</p>
            <p className={commun.secret} aria-label="Clé secrète">
              {secret.secret}
            </p>
            <details>
              <summary>Adresse de configuration (otpauth)</summary>
              <p className={commun.secret}>{secret.uri_otpauth}</p>
            </details>
            <Champ
              id="code-confirmation"
              libelle="Code à six chiffres affiché par l'application"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.trim())}
              required
            />
          </Formulaire>
        )}
      </main>
    </div>
  );
}
